import CameraContainer, { base64ToFile } from "@frontend/components/ocr/Camera";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@frontend/shared/components/ui/dialog";
import { Camera } from "lucide-react";
import { useTranslations } from "next-intl";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  type Control,
  type ControllerRenderProps,
  type FieldErrors,
  type UseFormGetValues,
  type UseFormSetValue,
  type UseFormTrigger,
  useWatch,
} from "react-hook-form";

import { formatRequiredLabel } from "./IntakeFormQuestion";
interface FileUploadZoneProps {
  onFilesUploaded?: (files: File[]) => void;
}

const dropzoneStyles: React.CSSProperties = {
  border: "1px dashed #cccccc",
  borderRadius: "4px",
  textAlign: "center",
  cursor: "pointer",
};

const FileUploadZone: React.FC<FileUploadZoneProps> = ({ onFilesUploaded }) => {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      // Handle the uploaded files
      if (onFilesUploaded) {
        onFilesUploaded(acceptedFiles);
      }
    },
    [onFilesUploaded],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  return (
    <div
      {...getRootProps()}
      style={dropzoneStyles}
      className="bg-[#F5F5F5] min-h-[140px] flex items-center justify-center w-full"
    >
      <input {...getInputProps()} />
      {isDragActive ? (
        <p>Drop the files here ...</p>
      ) : (
        <p className="text-[#989898] text-sm font-light">
          Drag a file here or Click to upload
        </p>
      )}
    </div>
  );
};

const fileToBase64 = (file: Blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

export const IntakeImageInput = ({
  isRequired,
  questionId,
  questionText,
  field,
  register,
  errors,
  getValues,
  setValue,
  trigger,
  control,
  updateProgress,
  isAdmin,
  initiateAutosave,
  hideQuestionText,
}: {
  isRequired: boolean;
  questionId: number;
  questionText: string;
  field: ControllerRenderProps<Partial<any>, string>;
  register: any;
  errors: FieldErrors<Partial<any>>;
  getValues: UseFormGetValues<Partial<any>>;
  setValue: UseFormSetValue<Partial<any>>;
  trigger: UseFormTrigger<Partial<any>>;
  control: Control<Partial<any>, any>;
  updateProgress: (updatedField: string, updatedValue: any) => void;
  isAdmin: boolean;
  initiateAutosave: (data: Partial<any>, hiddenQuestions?: Set<number>) => void;
  disabled?: boolean;
  hideQuestionText?: boolean;
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  const handleFilesUploaded = async (files: File[]) => {
    // Process the files as needed
    setUploadedFiles(files);
    const base64String = await fileToBase64(files[0]);
    setValue(`${questionId}`, base64String);
    updateProgress(field.name, base64String);
    trigger(`${questionId}`);
  };

  const [isDialogueOpen, setIsDialogueOpen] = useState(false);
  const imageWatch = useWatch({ control, name: `${questionId}`, exact: true });

  useEffect(() => {
    if (imageWatch && uploadedFiles.length === 0) {
      setUploadedFiles([base64ToFile(imageWatch, "uploaded_image")]);
    }
    initiateAutosave({ ...getValues(), [field.name]: imageWatch });
  }, [imageWatch]);

  const t = useTranslations("IntakeForm");

  return (
    <div className="flex flex-col gap-2 w-full">
      <p
        className="text-sm text-black"
        data-pdflabel="image-label"
        data-questionid={questionId}
      >
        {formatRequiredLabel(questionText, isRequired, hideQuestionText)}
      </p>

      {uploadedFiles.length > 0 ? (
        <div className="w-full flex flex-col border border-[#e1e1e1] rounded-sm p-4 bg-[#F5F5F5] relative gap-2">
          {uploadedFiles.map((file, index) => (
            <>
              <img
                key={`img-${file.name}`}
                src={URL.createObjectURL(file)}
                alt={`preview ${index}`}
              />
              <div
                className="flex w-full justify-between"
                key={`data-${file.name}`}
              >
                <p className="font-mono">{file.name}</p>
                {!isAdmin && (
                  <button
                    type="button"
                    onClick={() => {
                      setUploadedFiles([]);
                      setIsDialogueOpen(false);

                      setValue(questionId.toString(), null);
                      updateProgress(field.name, null);
                    }}
                  >
                    <p className="text-black text-sm font-light underline">
                      {t("Remove")}
                    </p>
                  </button>
                )}
              </div>
            </>
          ))}
        </div>
      ) : (
        <div className="w-full flex flex-col h-full gap-2 items-center mb-2">
          <FileUploadZone onFilesUploaded={handleFilesUploaded} />

          <p className="text-[#898989] font-light">Or</p>
          <Dialog
            open={isDialogueOpen}
            onOpenChange={(e) => {
              setIsDialogueOpen(e);
            }}
          >
            <div className="flex w-full gap-4 h-12 rounded-sm lg:mb-0 sm:mb-8">
              <div className="w-full relative bg-[#D9D9D9] flex-1 rounded-sm ">
                <DialogTrigger asChild={true}>
                  <div className="w-full flex bg-black items-center justify-center h-full gap-3 rounded-md cursor-pointer">
                    <Camera color="white" strokeWidth={1} />{" "}
                    <p className="text-white text-sm font-light">
                      {t("TakePhoto")}
                    </p>
                  </div>
                </DialogTrigger>

                <DialogContent className="w-auto flex items-center justify-center top-1/2 -translate-x-1/2 -translate-y-1/2  bg-[#B1B1B199] p-0">
                  <CameraContainer
                    documentType={"ID"}
                    setCameraStatus={setIsDialogueOpen}
                    setImageFile={handleFilesUploaded}
                    questionText={questionText}
                  />
                </DialogContent>
              </div>

              {isDialogueOpen && (
                <div
                  className="w-full flex items-center justify-center fixed top-12 h-[calc(100%-48px)] z-10 left-0 bg-[#B1B1B199]"
                  style={{ backdropFilter: "blur(1.5px)" }}
                />
              )}
            </div>
          </Dialog>
        </div>
      )}
    </div>
  );
};
